import { useState } from 'react';
import { Loader2, Search, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function EntityStampDistributionPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.entityStampDistribution();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Entity Stamp Distribution</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Distinct entity_id values stamped on each collection with counts.
          Flags null/missing as &quot;unassigned&quot; and values not in ENTITY_DEFS as unresolvable.
        </p>

        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Run
        </Button>

        {result && (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <span>Entities in ENTITY_DEFS: <strong>{result.entity_count_in_defs}</strong></span>
              <span>Collections scanned: <strong>{result.collections?.length}</strong></span>
            </div>

            {result.collections?.map((col) => (
              <details key={`${col.collection}-${col.field}`} className="border rounded-lg">
                <summary className="px-3 py-2 cursor-pointer text-sm font-medium flex items-center gap-2 hover:bg-muted/30">
                  <span className="font-mono">{col.collection}.{col.field}</span>
                  <Badge variant="outline" className="text-xs">{col.total_docs} docs</Badge>
                  <Badge variant="outline" className="text-xs">{col.distinct_count} distinct</Badge>
                  {col.null_or_missing > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {col.null_or_missing} unassigned
                    </Badge>
                  )}
                  {col.unresolvable_values?.length > 0 && (
                    <Badge variant="destructive" className="text-xs flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {col.unresolvable_values.length} unresolvable
                    </Badge>
                  )}
                </summary>
                <div className="px-3 pb-3">
                  <div className="overflow-x-auto border rounded mt-2">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1.5 font-medium">entity_id</th>
                          <th className="text-left px-2 py-1.5 font-medium">Label</th>
                          <th className="text-right px-2 py-1.5 font-medium">Count</th>
                          <th className="text-center px-2 py-1.5 font-medium">In ENTITY_DEFS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {col.buckets?.map((b, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">
                              {b.entity_id ?? <span className="text-muted-foreground italic">null</span>}
                            </td>
                            <td className="px-2 py-1">{b.label}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{b.count}</td>
                            <td className="px-2 py-1 text-center">
                              {b.entity_id === null ? (
                                <span className="text-muted-foreground">-</span>
                              ) : b.in_entity_defs ? (
                                <span className="text-green-600">yes</span>
                              ) : (
                                <span className="text-red-600 font-medium">NO</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
